import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import LanguageHelper, { type SurveyTranslations } from '../utils/languageHelper';

interface LanguageState extends SurveyTranslations {
  // Language metadata
  code: string;
  name: string;
  nativeName: string
  countryCode: string;
  flagUrl: string;

  // Actions
  setLanguage: (languageCode: string) => void;
  formatPageOf: (current: number, total: number) => string;
  getFlagUrl: (size?: 20 | 40 | 80 | 160) => string;
  reset: () => void;
}

// Helper function to initialize state from language code
const initializeLanguageState = (languageCode: string = 'en') => {
  const language = LanguageHelper.getLanguage(languageCode) || LanguageHelper.getLanguage('en')!;
  const translations = LanguageHelper.getTranslations(languageCode);

  return {
    code: language.code,
    name: language.name,
    nativeName: language.nativeName,
    countryCode: language.countryCode,
    flagUrl: language.flagUrl,
    ...translations, // Spread all translation fields
  };
};

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set, get) => ({
      // Initialize with English as default
      ...initializeLanguageState('en'),

      // Set a new language by code
      setLanguage: (languageCode: string) => {
        if (!LanguageHelper.isLanguageSupported(languageCode)) {
          console.warn(`Language code "${languageCode}" is not supported. Falling back to English.`);
          languageCode = 'en';
        }

        set(initializeLanguageState(languageCode));
      },

      // Format the "Page X of Y" string with current values
      formatPageOf: (current: number, total: number) => {
        const state = get();
        return state.pageOf
          .replace('{current}', current.toString())
          .replace('{total}', total.toString());
      },

      // Get flag URL with custom size
      getFlagUrl: (size: 20 | 40 | 80 | 160 = 40) => {
        const state = get();
        return LanguageHelper.getFlagUrl(state.code, size);
      },

      // Reset to default language (English)
      reset: () => {
        set(initializeLanguageState('en'));
      },
    }),
    {
      name: 'language-storage', // Key in localStorage
      storage: createJSONStorage(() => localStorage),
      // Only persist the language code, rehydrate everything else
      partialize: (state) => ({ code: state.code }),
      onRehydrateStorage: () => (state) => {
        // After rehydrating from storage, reinitialize all translations
        if (state) {
          const languageCode = state.code || 'en';
          Object.assign(state, initializeLanguageState(languageCode));
        }
      },
    }
  )
);

// Selector hooks for better performance (only re-render when specific values change)
export const useLanguageCode = () => useLanguageStore((state) => state.code);
export const useLanguageName = () => useLanguageStore((state) => state.name);
export const useLanguageNativeName = () => useLanguageStore((state) => state.nativeName);
export const useFlagUrl = () => useLanguageStore((state) => state.flagUrl);

// Translation selector hooks
export const useTranslation = (key: keyof SurveyTranslations) =>
  useLanguageStore((state) => state[key]);

export const useTranslations = () => useLanguageStore((state) => {
  // Extract only the translation fields (not metadata or action functions)
  const { code, name, nativeName, countryCode, flagUrl, setLanguage, formatPageOf, getFlagUrl, reset, ...translations } = state;
  return translations as SurveyTranslations;
});

// Action selector hooks
export const useSetLanguage = () => useLanguageStore((state) => state.setLanguage);
export const useFormatPageOf = () => useLanguageStore((state) => state.formatPageOf);

export default useLanguageStore;
